import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Award, TrendingUp, Building2, CheckCircle2, Clock } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Footer } from "../components/Footer";
import api from "../lib/api";
import { pick } from "../lib/pick";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="card-dark p-6" data-testid={testId}>
    <span className="w-10 h-10 rounded-2xl bg-[#EEF4FF] text-[#1D4ED8] flex items-center justify-center"><Icon className="w-5 h-5" /></span>
    <p className="font-heading font-black text-3xl text-[#0B1B3A] mt-4">{value}</p>
    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#64748B] mt-1">{label}</p>
  </div>
);

export default function Team() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/team").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === null) return <div className="max-w-7xl mx-auto px-6 py-24 text-[#64748B]">…</div>;
  if (data === false) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="team-empty">
        <Building2 className="w-10 h-10 text-[#1D4ED8] mx-auto" />
        <h1 className="font-heading font-black text-3xl mt-5 text-[#0B1B3A]">{t("team.noTeam")}</h1>
        <p className="text-[#475569] mt-3">{t("team.noTeamBody")}</p>
      </div>
    );
  }

  const { stats, members, courses } = data;
  return (
    <div data-testid="team-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 pt-14 pb-20">
        <span className="eyebrow"><span className="w-7 h-px bg-[#1D4ED8]" />{t("team.eyebrow")}</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl mt-5 text-[#0B1B3A]" data-testid="team-title">{data.name}</h1>
        <p className="text-base text-[#475569] mt-4 max-w-2xl">{t("team.subtitle")}</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          <Stat icon={Users} label={t("team.seats")} value={`${stats.used}/${stats.seats}`} testId="team-stat-seats" />
          <Stat icon={CheckCircle2} label={t("team.registered")} value={stats.registered} testId="team-stat-registered" />
          <Stat icon={TrendingUp} label={t("team.avgProgress")} value={`${stats.avg_progress}%`} testId="team-stat-progress" />
          <Stat icon={Award} label={t("team.certificates")} value={stats.certificates} testId="team-stat-certs" />
        </div>

        <div className="card-dark mt-10 overflow-hidden" data-testid="team-table">
          <div className="hidden md:grid grid-cols-[1.4fr_repeat(var(--cols),1fr)_0.6fr] gap-4 px-6 py-3 bg-[#F8FAFC] text-[10px] font-bold uppercase tracking-[0.18em] text-[#64748B]" style={{ "--cols": courses.length }}>
            <span>{t("team.member")}</span>
            {courses.map((c) => <span key={c.id} className="truncate">{pick(c, "title", lang)}</span>)}
            <span className="text-right">{t("team.certs")}</span>
          </div>
          {members.map((m, i) => (
            <motion.div key={m.email} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="grid md:grid-cols-[1.4fr_repeat(var(--cols),1fr)_0.6fr] gap-4 px-6 py-4 border-t border-[#E2E8F0] items-center" style={{ "--cols": courses.length }} data-testid={`team-member-${i}`}>
              <div className="min-w-0">
                <p className="font-bold text-sm text-[#0B1B3A] truncate">{m.name || m.email}</p>
                <p className="text-xs text-[#64748B] truncate">{m.email}</p>
                {!m.registered && <span className="badge-outline mt-1 !text-[9px]"><Clock className="w-3 h-3" /> {t("team.pending")}</span>}
              </div>
              {m.registered ? m.courses.map((c) => (
                <div key={c.course_id} data-testid={`team-progress-${i}-${c.course_id}`}>
                  <p className="md:hidden text-[10px] font-bold uppercase tracking-[0.14em] text-[#64748B] mb-1">{pick(c, "title", lang)}</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full bg-[#E2E8F0] overflow-hidden"><div className="h-full bg-[#1D4ED8] rounded-full" style={{ width: `${c.progress}%` }} /></div>
                    <span className="text-xs font-bold text-[#0B1B3A] w-10 text-right">{c.progress}%</span>
                  </div>
                  <p className="text-[11px] text-[#64748B] mt-1">{c.certified ? <span className="text-[#1D4ED8] font-bold">✓ {t("team.certified")} · {c.score}%</span> : `${c.completed}/${c.total} ${t("learn.lessons")}`}</p>
                </div>
              )) : courses.map((c) => <span key={c.id} className="text-xs text-[#94A3B8]">—</span>)}
              <p className="font-heading font-black text-xl text-[#0B1B3A] md:text-right">{m.certificates}</p>
            </motion.div>
          ))}
          {members.length === 0 && <p className="p-8 text-sm text-[#64748B] text-center">{t("team.noMembers")}</p>}
        </div>
      </div>
      <Footer />
    </div>
  );
}
